/**
 * Single shared renderer for the customer configurator — used by BOTH the
 * live customer form (``DynamicPrimaryConfigurator``) AND the admin schema-editor
 * preview (``ProcessSchemaEditor``), so the two can never diverge.
 *
 * It renders an entire ``configurator_schema`` (``{fields:[{key,kind,…}]}``)
 * **strictly in field order** — so the admin's drag-sorted field order is exactly
 * what the customer sees — and owns the special-field renderers that used to be
 * hardcoded in the orchestrator:
 *
 *   - ``kind:"material_ref"`` → catalog material dropdown (options filtered by
 *     ``process_type``). testid ``cfg-dynamic-primary-material``. Writes the
 *     reserved ``material_code`` key regardless of the field's own key.
 *   - a field whose merged feasibility presets exist (live) OR which is flagged
 *     ``tunable`` (preview) → preset/slider via ``TunablePresetsSection``.
 *   - the ``quantity`` field → plain number input. testid
 *     ``cfg-dynamic-primary-quantity``.
 *   - everything else → the generic ``DynamicConfigurator`` (one field at a time,
 *     keeping its enum / scalar / boolean / text renderers + visible_if).
 *
 * Tunable ownership at runtime is driven by the presence of merged presets
 * (``tunables[key]``). The schema ``tunable`` flag only matters in ``disabled``
 * preview mode, where there is no feasibility data: such fields render a disabled
 * "preset / slider" placeholder. In ``disabled`` mode the material options come
 * from ``materialOptionsOverride`` (the admin has no priced catalog).
 */

import { useMemo, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { resolveI18nText } from "@/components/admin/I18nField";
import { Dropdown, type DropdownOption } from "@/components/ui/Dropdown";
import { resolveBoundColors } from "@/lib/boundColor";
import { formatNumber } from "@/lib/displayFormat";
import { offeredAxisValues } from "@/lib/offeredAxisValues";
import { cx } from "@/lib/utils";

import { ChoiceCards, type ChoiceOption } from "./ChoiceCards";
import { DynamicConfigurator } from "./DynamicConfigurator";
import { InteractiveViewer, MetricsView } from "./InteractiveViewer";
import { TunablePresetsSection } from "./TunablePresetsSection";

import type { TunablePreset } from "@/api/queries/pricing";
import type { AnalysisSnapshot, Catalog } from "@/api/types";

/** Raw configurator-schema field as it arrives from the platform UI. Only the
 *  keys this component dispatches on are read here; ``DynamicConfigurator``
 *  parses the rest. */
interface RawField {
  key?: unknown;
  kind?: unknown;
  tunable?: unknown;
  label_cs?: unknown;
  label_en?: unknown;
  description_cs?: unknown;
  description_en?: unknown;
  /** Legacy single-language help-text key (pre-bilingual); treated as Czech. */
  description?: unknown;
  unit?: unknown;
  /** Jednotka pro čtenáře (KAN-1446). ``unit`` řídí normalizaci ve vzorcích,
   *  tahle jen text u pole — proto se zobrazuje přednostně. */
  display_unit?: unknown;
  options_source?: unknown;
  /**
   * Popsané hodnoty volby (KAN-1149) — teprve ony z pole udělají karty.
   * Pole s holým ``enum_values`` zůstává rozbalovacím seznamem, takže žádný
   * existující konfigurátor se tímhle nemění; karty si operace zapne tím,
   * že hodnotám dopíše, co znamenají.
   */
  enum_options?: unknown;
  /** ``hodnota → {cs, en}`` z registru vlastností materiálu (KAN-1670). */
  enum_labels?: unknown;
}

/** ``enum_options`` → karty, nebo ``null`` když je autor nedeklaroval. */
function choiceOptionsOf(f: RawField, lang: string): ChoiceOption[] | null {
  if (!Array.isArray(f.enum_options) || f.enum_options.length === 0) return null;
  // KAN-1670 — jména z registru vlastností (``enum_labels`` od API) pojmenují
  // hodnotu, kterou autor karty nepopsal.
  const registry =
    f.enum_labels && typeof f.enum_labels === "object"
      ? (f.enum_labels as Record<string, { cs?: unknown; en?: unknown } | undefined>)
      : {};
  const out: ChoiceOption[] = [];
  for (const raw of f.enum_options) {
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;
    const value = typeof o.value === "string" ? o.value : "";
    if (!value) continue;
    const named = registry[value];
    const label =
      resolveI18nText(
        {
          cs:
            (typeof o.label_cs === "string" && o.label_cs) ||
            (typeof named?.cs === "string" ? named.cs : ""),
          en:
            (typeof o.label_en === "string" && o.label_en) ||
            (typeof named?.en === "string" ? named.en : ""),
        },
        lang,
      ) || value;
    const description = resolveI18nText(
      {
        cs: typeof o.description_cs === "string" ? o.description_cs : "",
        en: typeof o.description_en === "string" ? o.description_en : "",
      },
      lang,
    );
    out.push({ value, label, description: description || null });
  }
  return out.length > 0 ? out : null;
}

/** ``options_source.material_property`` → property key, když je ``choice`` pole
 *  nabindované na per-materiál seznam možností (jinak ``undefined``). */
function boundOptionPropertyOf(f: RawField): string | undefined {
  const src = f.options_source;
  if (!src || typeof src !== "object" || Array.isArray(src)) return undefined;
  const key = (src as { material_property?: unknown }).material_property;
  return typeof key === "string" && key.trim() ? key : undefined;
}

interface Props {
  /** Full ``configurator_schema`` (``{fields:[…]}``). */
  schema?: Record<string, unknown> | null;
  /** Customer catalog; ``null`` in preview (options come from the override). */
  catalog?: Catalog | null;
  analysis?: AnalysisSnapshot | null;
  processType: string;
  /** Merged per-machine tunables from ``/pricing/feasibility`` (empty in preview). */
  tunables: Record<string, TunablePreset>;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  lang: string;
  /** Non-interactive preview mode (admin schema editor). */
  disabled?: boolean;
  /** ``false`` = interactive, but never asks the tenant ``/pricing/*``
   *  endpoints (the platform sample has no tenant — KAN-1561). */
  livePricing?: boolean;
  /** ``true`` = srovnávací ceny voleb počkají, až dorazí hlavní cena
   *  (KAN-1642) — viz ``ChoiceCards``. */
  deferLivePricing?: boolean;
  /** Placeholder material options for preview (no real catalog). */
  materialOptionsOverride?: DropdownOption<string>[];
  /** Presigned URL to the module's out_viz view-spec (``quote.view_spec_url``).
   *  Fills a ``kind:"visualization"`` slot live. ``null``/undefined = the module
   *  produced no view-spec → the slot renders nothing (it owns its own content). */
  viewSpecUrl?: string | null;
  /** Key of the process's DECLARED variant axis (``manifest.variant_param.key``).
   *  The component recognises no axis of its own — omit it and the field renders
   *  as any other scalar. Its offered values come from the SELECTED material, so
   *  a preview with no catalog degrades on its own. */
  axisKey?: string | null;
  /** Unit shown next to each offered axis value (``manifest.variant_param.unit``). */
  axisUnit?: string | null;
  /** Customer-editable keys for THIS graph node
   *  (``ProcessChainNodePolicy.public_input_dimension_keys``, resolved server-side).
   *
   *  A tenant may narrow a node — e.g. lock a dimension to an operational value
   *  and let the customer pick only the rest. The schema is per PROCESS and knows
   *  nothing about that, but the cart gate is per NODE: submitting a changed value
   *  for a locked key fails with ``public_input_locked``. Rendering a control the
   *  customer must not touch is therefore an invitation to a dead end, so a
   *  narrowed node hides the field entirely.
   *
   *  Empty / omitted = no narrowing (every schema field renders). */
  allowedKeys?: string[] | null;
  /** KAN-1903 — the shop's per-line quantity cap (admin Order Limits), the
   *  number the cart enforces. Bounds the quantity field and names the limit
   *  as soon as the customer types past it. ``null``/omitted = no cap. */
  maxQuantity?: number | null;
}

/** The schema's field list, or ``[]`` for a schema that declares none.
 *
 *  Exported because the orchestrator has to answer the same question BEFORE it
 *  renders anything: a field-less schema produces no inputs at all (material and
 *  quantity are themselves fields), so there is nothing to configure. Asking it
 *  through this function keeps one definition of "has fields" — notably, mere
 *  non-emptiness of ``configurator_schema`` is NOT it. */
export function fieldList(
  schema: Record<string, unknown> | null | undefined,
): RawField[] {
  const fields = (schema as { fields?: unknown } | null | undefined)?.fields;
  return Array.isArray(fields) ? (fields as RawField[]) : [];
}

const keyOf = (f: RawField): string => String(f.key ?? "");

function labelOf(f: RawField, lang: string, fallback: string): string {
  const cs = typeof f.label_cs === "string" ? f.label_cs : "";
  const en = typeof f.label_en === "string" ? f.label_en : "";
  return resolveI18nText({ cs, en }, lang) || fallback;
}

/** Optional bilingual help text under a field (admin-authored on the dimension's
 *  ``presentation_json``). Empty string when none — caller skips rendering. */
function descriptionOf(f: RawField, lang: string): string {
  const legacy = typeof f.description === "string" ? f.description : "";
  const cs = (typeof f.description_cs === "string" ? f.description_cs : "") || legacy;
  const en = typeof f.description_en === "string" ? f.description_en : "";
  return resolveI18nText({ cs, en }, lang);
}

export function CustomerConfiguratorForm({
  schema,
  catalog,
  analysis = null,
  processType,
  tunables,
  value,
  onChange,
  lang,
  disabled = false,
  livePricing = true,
  deferLivePricing = false,
  materialOptionsOverride,
  viewSpecUrl,
  axisKey,
  axisUnit,
  allowedKeys,
  maxQuantity = null,
}: Props): JSX.Element {
  const { t } = useTranslation("quote");
  const allFields = fieldList(schema);
  // Narrowed node → render only what this node lets the customer change. A
  // visualization slot is not an input and stays regardless.
  const fields =
    allowedKeys && allowedKeys.length > 0
      ? allFields.filter(
          (f) => f.kind === "visualization" || allowedKeys.includes(keyOf(f)),
        )
      : allFields;
  const tunableKeys = useMemo(() => new Set(Object.keys(tunables)), [tunables]);

  // ``fieldKey → chosen-option hex`` for option fields (the "Barva" picker). A
  // ``model3d`` viz block bound to one of these (``data.color_field``) tints to the
  // live pick; recomputed as the customer changes material / option so the 3D
  // preview follows the swatch.
  const materialCode = typeof value.material_code === "string" ? value.material_code : "";
  // Both the option picker and the variant-axis select read their offered values
  // off the SELECTED material. ``undefined`` in preview (no catalog) — every
  // consumer degrades on that rather than special-casing preview mode.
  const selectedMaterial = materialCode
    ? (catalog?.materials ?? []).find((m) => m.code === materialCode)
    : undefined;
  const boundColors = useMemo(
    () => resolveBoundColors(schema, catalog, materialCode, value),
    [schema, catalog, materialCode, value],
  );

  // A field is rendered as a tunable when feasibility returned presets for its
  // key (live), or — in preview, where there's no feasibility — when it's
  // flagged ``tunable``. The flag does NOT force tunable rendering live: a
  // tunable field with no capable machine falls back to its plain scalar input.
  const ownsTunable = (f: RawField): boolean =>
    tunableKeys.has(keyOf(f)) || (disabled && f.tunable === true);

  const setKey = (key: string, v: unknown) => onChange({ ...value, [key]: v });

  const materialOptions: DropdownOption<string>[] =
    materialOptionsOverride ??
    (catalog?.materials ?? [])
      .filter((m) => (m.process_types ?? []).includes(processType))
      .map((m) => ({ value: m.code, label: m.display_name }));

  const renderField = (f: RawField): JSX.Element | null => {
    const key = keyOf(f);

    // Visualization slot — the platform only reserves the space; the compute
    // module fills it via its out_viz view-spec. Live: render the interactive
    // viewer when a view-spec exists, otherwise nothing (module has no viz part).
    // Preview (admin): a sample of the EXACT customer renderer so "co uvidí
    // uživatel" shows the reserved space without a live quote.
    if (f.kind === "visualization") {
      const label = labelOf(f, lang, t("config.visualization", { defaultValue: "Vizualizace" }));
      if (disabled) {
        return (
          <div key={key} className="space-y-1.5" data-testid="cfg-visualization-preview">
            <span className="block text-sm font-medium text-ink">{label}</span>
            <MetricsView
              data={{
                metrics: [
                  { label: t("config.visualizationSampleRow", { defaultValue: "Ukázková metrika" }), value: "—" },
                ],
              }}
            />
            <span className="block text-[10.5px] text-ink-4">
              {t("config.visualizationNote", {
                defaultValue: "Ukázka — skutečný obsah vyplní výpočetní modul (jeho viz část).",
              })}
            </span>
          </div>
        );
      }
      if (!viewSpecUrl) return null;
      return (
        <div key={key} className="space-y-1.5" data-testid="cfg-visualization">
          <span className="block text-sm font-medium text-ink">{label}</span>
          <InteractiveViewer url={viewSpecUrl} boundColors={boundColors} />
        </div>
      );
    }

    if (f.kind === "material_ref") {
      const label = labelOf(f, lang, t("config.material", { defaultValue: "Materiál" }));
      return (
        <label className="block" key={key}>
          <span className="mb-1 block text-sm font-medium text-ink">{label}</span>
          <Dropdown
            ariaLabel={label}
            options={materialOptions}
            value={typeof value.material_code === "string" ? value.material_code : ""}
            onChange={(next) => setKey("material_code", String(next))}
            disabled={disabled}
            placeholder={
              disabled
                ? t("config.materialPlaceholder", { defaultValue: "— materiál z ceníku —" })
                : undefined
            }
            className="w-full"
            testId="cfg-dynamic-primary-material"
          />
        </label>
      );
    }

    if (key === "quantity") {
      const label = labelOf(f, lang, t("config.quantity", { defaultValue: "Počet kusů" }));
      const qty = typeof value.quantity === "number" ? value.quantity : 1;
      // KAN-1903 — not clamped: the customer sees what they typed and why it
      // is refused. The price preview answers ``line_qty_exceeds_max`` from
      // the same setting, so "Add to cart" stays disabled meanwhile.
      const overCap = maxQuantity !== null && qty > maxQuantity;
      return (
        <label className="block" key={key}>
          <span className="mb-1 block text-sm font-medium text-ink">{label}</span>
          <input
            type="number"
            min={1}
            max={maxQuantity ?? undefined}
            disabled={disabled}
            value={qty}
            aria-invalid={overCap || undefined}
            onChange={(event) =>
              setKey("quantity", Math.max(1, Number(event.target.value) || 1))
            }
            // KAN-2020 — ``bg-bg`` (pozadí stránky) dělalo pole šedé, jako
            // zamčené; editovatelné pole má povrch jako ostatní vstupy.
            className={cx(
              "h-9 w-full rounded border bg-surface px-2 text-sm text-ink hover:border-ink-3 disabled:opacity-60",
              overCap ? "border-danger" : "border-border-strong",
            )}
            data-testid="cfg-dynamic-primary-quantity"
          />
          {overCap && (
            <span
              className="mt-1 block text-xs text-danger"
              data-testid="cfg-dynamic-primary-quantity-over-cap"
            >
              {t("config.quantityOverCap", {
                max: maxQuantity.toLocaleString(lang),
              })}
            </span>
          )}
        </label>
      );
    }

    if (ownsTunable(f)) {
      // Preview (no feasibility data) → disabled "preset / slider" placeholder.
      if (!tunableKeys.has(key)) {
        const unit =
          typeof f.display_unit === "string" && f.display_unit.trim()
            ? f.display_unit.trim()
            : typeof f.unit === "string"
              ? f.unit
              : "";
        return (
          <label
            key={key}
            className="flex flex-col gap-1 text-[12px] text-ink-3"
            data-testid={`tunable-${key}-preview`}
          >
            <span>
              {labelOf(f, lang, key)}
              {unit ? ` (${unit})` : ""}
            </span>
            <select
              disabled
              defaultValue=""
              className="h-9 rounded border border-border bg-surface px-2 text-[13px] text-ink"
            >
              <option value="">
                {t("config.tunablePlaceholder", { defaultValue: "— preset / slider —" })}
              </option>
            </select>
            <span className="text-[10.5px] text-ink-4">
              {t("config.tunableNote", {
                defaultValue: "Presety, výchozí a slider vyplní tenant u stroje.",
              })}
            </span>
          </label>
        );
      }
      // Live: one preset/slider control for this key (rendered in schema order).
      return (
        <TunablePresetsSection
          key={key}
          tunables={{ [key]: tunables[key] }}
          config={value}
          onChange={onChange}
          lang={lang}
        />
      );
    }

    // Option picker — a ``choice`` field bound to a material option-source
    // property (``color_options`` / ``option_list``). Options come from the
    // SELECTED material; the chosen ``code`` is written to the field's own key
    // (e.g. ``color_code``) so it flows into pricing (``color_surcharges[code]``)
    // + the cart/order snapshot unchanged. The swatch adapts per option: hex →
    // color dot, image → thumbnail, neither → plain label.
    // Popsaná volba → karty s dopadem na cenu (KAN-1149). Stojí PŘED vazbou
    // na materiálovou vlastnost: ta bere hodnoty ze zvoleného materiálu,
    // kdežto tyhle autoruje operace, a míchat obojí v jednom poli nemá smysl.
    const choiceOptions = choiceOptionsOf(f, lang);
    if (choiceOptions) {
      return (
        <ChoiceCards
          key={key}
          fieldKey={key}
          label={labelOf(f, lang, key)}
          // No ``description`` here: the form renders every field's help once,
          // under the field (``cfg-field-help-*``) — passing it too drew it twice.
          options={choiceOptions}
          value={value[key]}
          onChange={(next) => onChange({ ...value, [key]: next })}
          config={value}
          processType={processType}
          analysis={analysis}
          disabled={disabled}
          livePricing={livePricing}
          deferLivePricing={deferLivePricing}
          testIdPrefix={`cfg-choice-${key}`}
        />
      );
    }

    const optionProperty = boundOptionPropertyOf(f);
    if (optionProperty) {
      const label = labelOf(f, lang, t("config.options", { defaultValue: "Volba" }));
      const current = typeof value[key] === "string" ? (value[key] as string) : "";
      const options = selectedMaterial?.option_sources?.[optionProperty] ?? [];
      return (
        <label className="block" key={key}>
          <span className="mb-1 block text-sm font-medium text-ink">{label}</span>
          {disabled ? (
            <div
              className="rounded border border-dashed border-border bg-surface px-2 py-1.5 text-[12px] text-ink-3"
              data-testid={`cfg-options-${key}-preview`}
            >
              {t("config.optionsPreview", {
                defaultValue: "— možnosti ze zvoleného materiálu —",
              })}
            </div>
          ) : options.length === 0 ? (
            <p
              className="text-xs text-ink-3"
              data-testid={`cfg-options-${key}-empty`}
            >
              {t("config.optionsEmpty", {
                defaultValue: "Pro tento materiál nejsou definované možnosti.",
              })}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid={`cfg-options-${key}`}>
              {options.map((opt) => {
                const on = current === opt.code;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={() => setKey(key, opt.code)}
                    aria-pressed={on}
                    aria-label={opt.label}
                    title={opt.label}
                    className={cx(
                      "flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs",
                      on
                        ? "border-ink ring-2 ring-ink/40"
                        : "border-border hover:bg-surface-2",
                    )}
                    data-testid={`cfg-options-${key}-opt-${opt.code}`}
                  >
                    {opt.swatch_image_url ? (
                      <img
                        aria-hidden
                        src={opt.swatch_image_url}
                        alt=""
                        className="h-4 w-4 rounded-full border border-border object-cover"
                      />
                    ) : opt.hex ? (
                      <span
                        aria-hidden
                        className="inline-block h-4 w-4 rounded-full border border-border"
                        style={{ background: opt.hex }}
                      />
                    ) : null}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
        </label>
      );
    }

    // The process's DECLARED variant axis is a CLOSED set: only the values the
    // tenant actually priced can be produced. Rendered as an ordinary scalar it
    // becomes a free number box, so a customer can type a value nobody priced,
    // watch a price appear, and then hit a 422 at add-to-cart. Offered values come
    // from the SELECTED material — the same per-material shape the option picker
    // above uses.
    //
    // Degrades to the generic scalar input on purpose whenever the closed set is
    // unknown: no axis declared, no catalog (the admin preview renders without
    // one), or a material the tenant priced no values for. Rendering an empty
    // select instead would leave those customers unable to order at all.
    const axisValues =
      axisKey && key === axisKey
        ? offeredAxisValues(selectedMaterial, processType, axisKey)
        : [];
    if (axisValues.length > 0) {
      const label = labelOf(f, lang, key);
      const current = typeof value[key] === "number" ? (value[key] as number) : "";
      return (
        <label className="block" key={key}>
          <span className="mb-1 block text-sm font-medium text-ink">{label}</span>
          <select
            value={String(current)}
            disabled={disabled}
            onChange={(event) => setKey(key, Number(event.target.value))}
            className="h-9 w-full rounded border border-border bg-surface px-2 text-[13px] text-ink"
            data-testid={`cfg-axis-${key}`}
          >
            {current === "" && <option value="">—</option>}
            {axisValues.map((v) => (
              <option key={v} value={String(v)}>
                {/* KAN-2017 — „1,5 mm", ne „1.5 mm". */}
                {axisUnit
                  ? `${formatNumber(v, { locale: lang, maxFractionDigits: 3 })} ${axisUnit}`
                  : formatNumber(v, { locale: lang, maxFractionDigits: 3 })}
              </option>
            ))}
          </select>
        </label>
      );
    }

    // Generic field (enum / scalar / boolean / text) — reuse DynamicConfigurator
    // one field at a time so its renderers + visible_if + validation are kept.
    return (
      <DynamicConfigurator
        key={key}
        config={value}
        analysis={analysis}
        lang={lang}
        onChange={onChange}
        schema={{ fields: [f] }}
      />
    );
  };

  return (
    <div className="space-y-3" data-testid="customer-configurator-form">
      {fields.map((f) => {
        const node = renderField(f);
        if (node === null) return null;
        const help = descriptionOf(f, lang);
        if (!help) return node;
        return (
          <div key={keyOf(f)} className="space-y-1">
            {node}
            <p
              className="text-[11.5px] leading-snug text-ink-3"
              data-testid={`cfg-field-help-${keyOf(f)}`}
            >
              {help}
            </p>
          </div>
        );
      })}
    </div>
  );
}

export default CustomerConfiguratorForm;
